import {
  getHeaderImage,
  MCMXCIV_LOGO_D4A847,
  eventTitle as getEventTitle,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { BlockRenderer } from './widgets/block-renderer';
import StyledBlockBox from './widgets/styled-block-box';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.siena;

const SIENA_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: {
    size: 'original',
    height: 24,
    align: 'center',
    background: 'var(--accent)',
    padding: '32px 56px',
  },
  [BLOCK_ID.title]: {
    color: 'var(--accent)',
    fontFamily: 'var(--heading-font)',
    fontSize: '48px',
    fontWeight: 400,
    lineHeight: '53px',
    textAlign: 'center',
    padding: '32px 56px 4px 56px',
  },
  [BLOCK_ID.salutation]: {
    color: 'var(--secondary-accent)',
    fontFamily: 'var(--body-font)',
    fontSize: '18px',
    fontWeight: 400,
    textAlign: 'center',
    padding: '4px 56px 32px 56px',
  },
  [BLOCK_ID.mainImage]: {
    size: 'original',
    width: 320,
    height: 320,
    align: 'center',
    padding: '0 56px',
    borderRadius: 'var(--image-radius)',
  },
  [BLOCK_ID.cta]: {
    height: '56px',
    lineHeight: '54px',
    buttonPadding: '0 32px',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--heading-font)',
    fontWeight: 400,
    fontSize: '16px',
  },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    fontWeight: 500,
    fontStyle: 'italic',
    textAlign: 'center',
    lineHeight: '18px',
    padding: '32px 64px',
  },
  [BLOCK_ID.divider]: { color: 'var(--secondary-accent)', padding: '0 56px' },
  [BLOCK_ID.tagline]: {
    color: 'var(--accent)',
    fontFamily: 'var(--heading-font)',
    fontSize: '14px',
    textTransform: 'uppercase',
    textAlign: 'center',
    padding: '48px 64px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

const INFO_LIST_BLOCK_ID = BLOCK_ID.infoList;

export const Siena = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const sessionEndTimestamp = event.sessions[0]?.end_timestamp;
  const durationMinutes =
    sessionTimestamp && sessionEndTimestamp ? Math.round((sessionEndTimestamp - sessionTimestamp) / 60000) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || MCMXCIV_LOGO_D4A847;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Instrument Serif", Georgia, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    'Urbanist, Arial, sans-serif'
  );
  const _salutation = salutation ?? (customizations?.salutation || getEventTitle(event, 'en'));
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: SIENA_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };
    const rowLabelStyle = { color: 'var(--accent)', fontFamily: 'var(--body-font)', fontWeight: 700, fontSize: '14px' };
    const rowDashStyle = { color: 'var(--secondary-accent)', fontFamily: 'var(--body-font)', fontSize: '14px' };
    const rowValueStyle = { color: 'var(--accent)', fontFamily: 'var(--body-font)', fontSize: '14px' };

    return (
      <table
        data-component="Siena"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--button-text': buttonText,
            '--heading': heading,
            '--body': body,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {blocks.map((block) => {
                        if (block.id === INFO_LIST_BLOCK_ID) {
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === blocks[0]?.id}
                              isLast={block.id === blocks[blocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <StyledBlockBox
                                blockId={block.id}
                                style={block.style}
                                defaults={{ margin: '32px 56px 12px 56px' }}
                                fitContent
                              >
                                <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                                  <tbody>
                                    {durationMinutes !== null && (
                                      <tr>
                                        <td style={{ paddingBottom: '24px' }}>
                                          <span style={rowLabelStyle}>{DEFAULTS.watchTimeLabel}</span>{' '}
                                          <span style={rowDashStyle}>—</span>{' '}
                                          <span style={rowValueStyle}>{durationMinutes} Minutes</span>
                                        </td>
                                      </tr>
                                    )}
                                    <tr>
                                      <td style={{ paddingBottom: '24px' }}>
                                        <span style={rowLabelStyle}>Available Until</span>{' '}
                                        <span style={rowDashStyle}>—</span> <span style={rowValueStyle}>On-Demand</span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>
                                        <span style={rowLabelStyle}>Topics Covered</span>{' '}
                                        <span style={rowDashStyle}>—</span>{' '}
                                        <span style={rowValueStyle}>{event.name}</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </StyledBlockBox>
                            </BlockShell>
                          );
                        }

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={SIENA_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Siena"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--button-text': buttonText,
          '--heading': heading,
          '--body': body,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td>
                    <LogoWidget
                      src={headerImg}
                      setImage={setHeaderImage}
                      size="original"
                      height={24}
                      align="center"
                      background="var(--accent)"
                      padding="32px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--accent)"
                      fontFamily="var(--heading-font)"
                      fontSize="48px"
                      fontWeight={400}
                      lineHeight="53px"
                      textAlign="center"
                      padding="32px 56px 4px 56px"
                    />

                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="var(--secondary-accent)"
                      fontFamily="var(--body-font)"
                      fontSize="18px"
                      fontWeight={400}
                      textAlign="center"
                      padding="4px 56px 32px 56px"
                    />

                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      size="original"
                      width={320}
                      height={320}
                      align="center"
                      padding="0 56px"
                      borderRadius={imageRadius}
                    />

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '32px 56px 12px 56px' }}>
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                {durationMinutes !== null && (
                                  <tr>
                                    <td style={{ paddingBottom: '24px' }}>
                                      <span
                                        style={{
                                          color: 'var(--accent)',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 700,
                                          fontSize: '14px',
                                        }}
                                      >
                                        {DEFAULTS.watchTimeLabel}
                                      </span>{' '}
                                      <span
                                        style={{
                                          color: 'var(--secondary-accent)',
                                          fontFamily: 'var(--body-font)',
                                          fontSize: '14px',
                                        }}
                                      >
                                        —
                                      </span>{' '}
                                      <span
                                        style={{
                                          color: 'var(--accent)',
                                          fontFamily: 'var(--body-font)',
                                          fontSize: '14px',
                                        }}
                                      >
                                        {durationMinutes} Minutes
                                      </span>
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <td style={{ paddingBottom: '24px' }}>
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontWeight: 700,
                                        fontSize: '14px',
                                      }}
                                    >
                                      Available Until
                                    </span>{' '}
                                    <span
                                      style={{
                                        color: 'var(--secondary-accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '14px',
                                      }}
                                    >
                                      —
                                    </span>{' '}
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '14px',
                                      }}
                                    >
                                      On-Demand
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontWeight: 700,
                                        fontSize: '14px',
                                      }}
                                    >
                                      Topics Covered
                                    </span>{' '}
                                    <span
                                      style={{
                                        color: 'var(--secondary-accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '14px',
                                      }}
                                    >
                                      —
                                    </span>{' '}
                                    <span
                                      style={{
                                        color: 'var(--accent)',
                                        fontFamily: 'var(--body-font)',
                                        fontSize: '14px',
                                      }}
                                    >
                                      {event.name}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td align="center" style={{ padding: '20px 56px' }}>
                            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      background: 'var(--button-color)',
                                      borderRadius: buttonRadius,
                                      height: '56px',
                                    }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        padding: '0 32px',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '16px',
                                          fontFamily: 'var(--heading-font)',
                                          fontWeight: 400,
                                          lineHeight: '54px',
                                          wordWrap: 'break-word',
                                          whiteSpace: 'nowrap',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="var(--body)"
                      fontFamily="var(--body-font)"
                      fontSize="12px"
                      fontWeight={500}
                      fontStyle="italic"
                      textAlign="center"
                      lineHeight="18px"
                      padding="32px 64px"
                    />

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '0 56px' }}>
                            <div
                              style={{ borderTop: '1px solid var(--secondary-accent)', fontSize: 0, lineHeight: '0' }}
                            >
                              &nbsp;
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <ParagraphWidget
                      value={DEFAULTS.taglineText}
                      color="var(--accent)"
                      fontFamily="var(--heading-font)"
                      fontSize="14px"
                      textTransform="uppercase"
                      textAlign="center"
                      padding="48px 64px"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
